<script lang="ts" module>
  export interface RecentSearch {
    query: string;
    timestamp: number;
  }

  const LOCAL_STORAGE_KEYS = {
    CHAT_HISTORY: 'blockSearchChatHistory',
    CHAT_ID: 'blockSearchChatId'
  };
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import type { Message } from '$lib/openai/types';
  import SearchInput from './SearchInput.svelte';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import { renderMarkdown } from '$lib/utils/markdown';
  import LoadingStatus from './LoadingStatus.svelte';
  import RecentSearches from './RecentSearches.svelte';
  import SearchResults from './SearchResults.svelte';

  let error: string | null = $state(null);
  let answer: string | null = null;
  let conversationHistory: Message[] = $state([]);
  let conversationContainer: HTMLDivElement | null = $state(null);
  let conversationId: string | null = $state(null);
  let recentSearches: RecentSearch[] = $state([]);
  let loading: boolean = $state(false);

  // Scroll to top of new message when added
  $effect(() => {
    if (conversationHistory.length > 0 && conversationContainer) {
      queueMicrotask(() => {
        if (conversationContainer) {
          const messages = conversationContainer.querySelectorAll('.message');
          const latestMessage = messages[messages.length - 1];

          if (latestMessage) {
            latestMessage.scrollIntoView({
              behavior: 'smooth',
              block: 'start' // top of the message
            });
          }
        }
      });
    }
  });

  async function handleSearch(query: string) {
    query = query.trim();
    const timestamp = Date.now();

    recentSearches = [
      { query, timestamp },
      ...recentSearches.filter((s) => s.query !== query)
    ].slice(0, 5); // Keep last 5 searches

    localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_HISTORY, JSON.stringify(recentSearches));

    loading = true;
    error = null;

    // Add user message to history
    conversationHistory = [
      ...conversationHistory,
      {
        role: 'user',
        content: query,
        timestamp
      }
    ];

    try {
      const requestBody = {
        query,
        conversationHistory: conversationHistory.map(({ role, content }) => ({ role, content })),
        conversationId
      };

      const response = await fetch('/api/search/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('AI search is currently unavailable, please try again later.');
      }

      const data = await response.json();
      answer = data.answer;
      const messageSources = data.results || [];

      // Add assistant response to history with sources
      conversationHistory = [
        ...conversationHistory,
        {
          role: 'assistant',
          content: answer || '',
          timestamp: Date.now(),
          sources: messageSources
        }
      ];
    } catch (err) {
      error =
        err instanceof Error
          ? err.message
          : 'AI search is currently unavailable, please try again later.';
    } finally {
      loading = false;
    }
  }

  const loadHistory = () => {
    const chatId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID);

    if (chatId) {
      conversationId = JSON.parse(chatId);
    } else {
      conversationId = crypto.randomUUID();
      localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, JSON.stringify(conversationId));
    }

    const history = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_HISTORY);
    if (history) {
      recentSearches = JSON.parse(history);
    }
  };

  onMount(() => {
    loadHistory();
  });
</script>

<div class="ai-search">
  <SearchInput placeholder="Ask me anything..." disableSubmit={loading} onSubmit={handleSearch} />

  <p class="disclaimer">
    <FontSizeDecorator size="caption">
      You are being connected to our automated chatbot which utilizes generative AI. By continuing,
      you recognize that
      <a href="/legal/privacy-notice" target="_blank" rel="noopener noreferrer">
        Block’s Privacy Notice
      </a>
      applies to your use of this tool and you agree to Block’s AI terms of use located
      <a
        href="https://squareup.com/us/en/legal/general/sq-generativeai-terms"
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </a>
      and
      <a
        href="https://cash.app/legal/us/en-us/gen-ai-terms"
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </a>.
    </FontSizeDecorator>
  </p>

  {#if conversationHistory.length > 0}
    <div class="conversation-container" bind:this={conversationContainer}>
      <div class="conversation-history" class:loading>
        {#each conversationHistory as message}
          <div class="message {message.role}">
            {#if message.role === 'user'}
              <div class="user-message">
                <FontSizeDecorator>{message.content}</FontSizeDecorator>
              </div>
            {:else}
              <div class="assistant-message">
                <FontSizeDecorator>{@html renderMarkdown(message.content)}</FontSizeDecorator>
                {#if message.sources && message.sources.length > 0}
                  <div class="sources">
                    <h6>
                      <HeadingSizeDecorator size="h6">Sources</HeadingSizeDecorator>
                    </h6>
                    <SearchResults
                      results={message.sources.map((source) => ({
                        title: source.title,
                        url: source.url,
                        text: source.description
                      }))}
                    />
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/each}
        <LoadingStatus {loading} />
      </div>
      {#if error}
        <p class="error">
          <FontSizeDecorator>
            {error}
          </FontSizeDecorator>
        </p>
      {/if}
    </div>
  {:else if recentSearches.length > 0}
    <RecentSearches {handleSearch} {recentSearches} />
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .ai-search {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 12px;
  }

  .conversation-container {
    flex: 1;
    overflow-y: auto;
    margin: 24px -12px;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .conversation-history {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 12px;
  }

  .message {
    &.user {
      align-self: flex-end;
      max-width: 85%;

      .user-message {
        background: $background-depth-2-color;
        padding: 12px 16px;
        border-radius: 12px 12px 0 12px;
      }
    }

    &.assistant {
      .assistant-message {
        color: $text-default-color;

        :global(p) {
          margin: 0 0 5px;
          &:last-child {
            margin-bottom: 0;
          }
        }

        :global(a) {
          color: $text-default-color;
        }

        :global(code) {
          background: $background-depth-2-color;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
        }

        :global(pre) {
          background: $background-depth-2-color;
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 16px 0;

          :global(code) {
            background: none;
            padding: 0;
          }
        }
      }
    }
  }

  .error {
    color: $text-warning-color;
    margin: 16px 0;
  }

  .disclaimer {
    color: $text-helper-color;
    margin: 8px 0 0;

    a {
      color: inherit;
      text-decoration: underline;
    }
  }

  .sources {
    margin: 24px -12px;

    h6 {
      border-top: 1px solid $border-depth-2-color;
      margin: 0 12px;
      padding: 12px 0;
    }
  }

  .query {
    flex: 1;
    min-width: 0; // allow flexbox to shrink below content size
    margin-right: 12px;
    text-align: left;

    :global(*) {
      overflow: hidden;
      text-overflow: ellipsis; // truncate longer queries
      white-space: nowrap;
      display: block;
      text-align: inherit;
    }
  }

  .timestamp {
    flex-shrink: 0; // prevent timestamp from being compressed
    white-space: nowrap;
  }
</style>
